import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { adminAPI } from '../../utils/api';

const AdminStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', class_id: '', search: '', page: 1 });
  const { success, error } = useToast();
  const navigate = useNavigate();

  useEffect(() => { loadStudents(); }, [filters]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getStudents(filters);
      setStudents(res.data.data);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, id, name) => {
    try {
      if (action === 'approve') { await adminAPI.approveStudent(id); success(`${name} approved!`); }
      else if (action === 'block') {
        const reason = prompt(`Reason for blocking ${name}?`);
        await adminAPI.blockStudent(id, reason || '');
        success(`${name} blocked.`);
      }
      else if (action === 'unblock') { await adminAPI.unblockStudent(id); success(`${name} unblocked.`); }
      else if (action === 'delete') {
        if (!window.confirm(`Permanently delete ${name}? This cannot be undone.`)) return;
        await adminAPI.deleteStudent(id);
        success(`${name} deleted.`);
      }
      loadStudents();
    } catch { error('Action failed.'); }
  };

  const STATUS_BADGE = {
    pending: { bg: '#FEF3C7', color: '#7C2D12', label: 'Pending' },
    active: { bg: '#E6F4EC', color: '#1B7F3E', label: 'Active' },
    blocked: { bg: '#FEE2E2', color: '#DC2626', label: 'Blocked' }
  };

  return (
    <AdminShell
      title="All Students"
      subtitle={`${total} total students registered`}
      actions={<button onClick={loadStudents} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>🔄 Refresh</button>}
    >
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '16px' }}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {['', 'active', 'pending', 'blocked'].map(s => (
            <button key={s} onClick={() => setFilters(p => ({ ...p, status: s, page: 1 }))}
              style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, border: filters.status === s ? 'none' : '1px solid #E2E8F0', background: filters.status === s ? '#1A3C6E' : 'white', color: filters.status === s ? 'white' : '#64748B', cursor: 'pointer' }}>
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <select
            value={filters.class_id}
            onChange={e => setFilters(p => ({ ...p, class_id: e.target.value, page: 1 }))}
            style={{ padding: '5px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, border: '1px solid #E2E8F0', background: 'white', cursor: 'pointer', outline: 'none' }}
          >
            <option value="">All Classes</option>
            {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
          </select>
          <input
            style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '11px', border: '1px solid #E2E8F0', outline: 'none', flex: 1, maxWidth: '220px' }}
            placeholder="🔍 Search by name or email..."
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value, page: 1 }))}
          />
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>No students found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Student', 'Class', 'Type', 'Mastery', 'Last Active', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ fontSize: '10px', fontWeight: 800, color: '#64748B', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map(s => {
                const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #F7F8FC' }} onMouseEnter={e => e.currentTarget.style.background = '#F7F8FC'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '9px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#E8F0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, color: '#1A3C6E', flexShrink: 0 }}>
                          {s.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700 }}>{s.name}</div>
                          <div style={{ fontSize: '10px', color: '#64748B' }}>{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '12px', padding: '9px 8px', color: '#64748B' }}>Class {s.class_id}</td>
                    <td style={{ padding: '9px 8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 800, background: s.registration_path === 'school' ? '#FEF3C7' : '#E8F0FB', color: s.registration_path === 'school' ? '#7C2D12' : '#1A3C6E', borderRadius: '20px', padding: '2px 7px' }}>
                        {s.registration_path === 'school' ? 'School' : 'Individual'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '50px', background: '#E2E8F0', borderRadius: '10px', height: '5px' }}>
                          <div style={{ background: '#1B7F3E', borderRadius: '10px', height: '100%', width: '40%' }}></div>
                        </div>
                        <span style={{ fontSize: '10px', fontWeight: 800, color: '#1B7F3E' }}>—</span>
                      </div>
                    </td>
                    <td style={{ fontSize: '11px', color: '#64748B', padding: '9px 8px' }}>
                      {s.last_active_at ? new Date(s.last_active_at).toLocaleDateString('en-IN') : 'Never'}
                    </td>
                    <td style={{ padding: '9px 8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 800, background: badge.bg, color: badge.color, borderRadius: '20px', padding: '2px 8px' }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '9px 8px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {s.status === 'pending' && <button onClick={() => handleAction('approve', s.id, s.name)} style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '20px', padding: '3px 8px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Approve</button>}
                        {s.status === 'active' && <button onClick={() => handleAction('block', s.id, s.name)} style={{ background: '#FEF3C7', color: '#7C2D12', border: 'none', borderRadius: '20px', padding: '3px 8px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Block</button>}
                        {s.status === 'blocked' && <button onClick={() => handleAction('unblock', s.id, s.name)} style={{ background: '#E6F4EC', color: '#1B7F3E', border: 'none', borderRadius: '20px', padding: '3px 8px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Unblock</button>}
                        <button onClick={() => handleAction('delete', s.id, s.name)} style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '20px', padding: '3px 8px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', fontSize: '12px', color: '#64748B' }}>
            <span>Showing {Math.min((filters.page - 1) * 20 + 1, total)}–{Math.min(filters.page * 20, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={filters.page === 1} onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer', background: 'white' }}>← Prev</button>
              <button disabled={filters.page * 20 >= total} onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer', background: 'white' }}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminStudentsPage;
